
import { apiFetch } from './api-client';
import type { GetUserPurchasesResponse, GetPurchaseByIdResponse, PurchaseDto } from '@/dto/index';

export type Purchase = {
  id: string;
  user_id: string;
  course_id: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed' | 'cancelled';
  payment_method: string;
  transaction_id?: string;
  created_at: string;
  payment_status?: string;
  payment_error?: string;
  payment_completed_at?: string;
  course?: {
    title: string;
    image_url: string;
  };
};

function mapPurchase(dto: PurchaseDto): Purchase {
  return {
    id: dto.id,
    user_id: dto.userId,
    course_id: dto.courseId,
    amount: dto.amount,
    status: dto.status as any,
    payment_method: dto.paymentMethod,
    transaction_id: dto.transactionId || undefined,
    created_at: dto.createdAt,
    payment_status: dto.paymentStatus,
    payment_error: dto.paymentError || undefined,
    payment_completed_at: dto.paymentCompletedAt || undefined,
  };
}

// Get purchase history for a user
export async function getUserPurchases(_userId: string): Promise<Purchase[]> {
  try {
    // Note: Backend currently gets userId from session, so _userId is ignored
    const data = await apiFetch<GetUserPurchasesResponse>('/api/user/purchases');
    return data.purchases.map(mapPurchase);
  } catch (error) {
    console.error('Error fetching user purchases:', error);
    return [];
  }
}

// Record a new purchase
export async function recordPurchase(
  userId: string,
  courseId: string,
  amount: number,
  status: 'completed' | 'pending' | 'failed' = 'completed',
  paymentMethod: string = 'test',
  transactionId?: string
): Promise<Purchase | null> {
  // This will be handled by Phase 4 (Payment creation).
  // For now, we return null or throw.
  console.warn("recordPurchase not implemented in new API yet (Phase 4 task)");
  return null;
}

/**
 * Fetch a single purchase by ID.
 */
export async function getPurchaseById(purchaseId: string): Promise<Purchase | null> {
  try {
    const data = await apiFetch<GetPurchaseByIdResponse>(`/api/user/purchases/${purchaseId}`);
    return mapPurchase(data.purchase);
  } catch (error) {
    console.error('Error fetching purchase by ID:', error);
    return null;
  }
}

/**
 * Check for payment status updates and refresh a purchase
 */
export async function refreshPurchaseStatus(purchaseId: string): Promise<Purchase | null> {
  return getPurchaseById(purchaseId);
}
